import { FC, ReactNode, useState } from 'react';
import { type HearingSubmissionSummary } from '@teachur/http-client';
import { SubmissionDetailModal } from '@new/common/ui/SubmissionDetail/SubmissionDetailModal';
import { toHearingSubmissionDetail } from '@new/common/ui/SubmissionDetail/submissionDetail.mappers';

interface HearingReviewItemProps {
  submission: HearingSubmissionSummary;
  isCancelled: boolean;
  statusBadge: ReactNode;
}

export const HearingReviewItem: FC<HearingReviewItemProps> = ({
  submission,
  isCancelled,
  statusBadge,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const canViewReview = isCancelled || (submission.grades && submission.grades.length > 0);

  const reviewDate = new Date(submission.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const reviewerName = submission.reviewedBy
    ? `${submission.reviewedBy.firstName} ${submission.reviewedBy.lastName}`
    : null;

  const cancelledByName = submission.rejectedBy
    ? `${submission.rejectedBy.firstName} ${submission.rejectedBy.lastName}`
    : null;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-(--neutral-black-NB100) p-4 shadow-xs transition-shadow hover:shadow-md">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="[font-size:var(--fs-l5)]! text-(--neutral-white-NW600)!">
            {isCancelled ? 'Cancelled' : 'Reviewed'}: {reviewDate}
          </span>

          {isCancelled
            ? cancelledByName && (
                <span className="[font-size:var(--fs-l5)]! text-(--danger-D700)!">
                  Cancelled by: {cancelledByName}
                </span>
              )
            : reviewerName && (
                <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)!">
                  Instructor: {reviewerName}
                </span>
              )}
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {statusBadge}

          {canViewReview && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="[font-size:var(--fs-l5)]! font-medium text-(--primary-O1000)! cursor-pointer hover:text-(--primary-O1100)! transition-colors bg-transparent border-0 p-0 underline"
            >
              View Review
            </button>
          )}
        </div>
      </div>

      <SubmissionDetailModal
        isOpen={isModalOpen}
        data={isModalOpen ? toHearingSubmissionDetail(submission, statusBadge) : null}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
